import { Direction } from "../../features/lazertrader/types";
import { Price, recordPriceDelimiter } from "../../price";
import { ContractIdentifier } from "./contracts";

type VirtualTradeFields = {
  trade_id: string;
  /**
   * Dashed uuid of the algo the fill belongs to. The subject carries the same
   * id with the dashes stripped
   * (`virtual_trades.jepx.<algo id hex>.<trade_id>`), so only the payload is
   * safe to display.
   */
  algo_id: string;
  internal_client_order_id: string;
  /** Either the algo id again or a spec label such as `limitorder:initialize`. */
  internal_order_text: string;
  /** The id lazer_trader gave the venue, plus the amend generation. */
  external_client_order_id: { inner: string; version: number };
  /** `lazer_trader-<n>|<external order id>:<version>|<algo id or spec>`. */
  external_order_text: string;
  /** The venue's own order id. */
  exchange_order_id: string;
  /** `Market` on every JEPX message so far. */
  virtual_trade_type: string;
  contract: ContractIdentifier;
  direction: Direction;
  quantity: { kilo_watts: number };
  price_delimiter?: number | null;
  /** When the venue matched it. */
  execution_time: string;
  /** `LazerTrader` for an algo fill. */
  trader: string;
  /** When lazerhub put it on the wire. */
  publish_time: string;
};

/**
 * A fill as it arrives. `price` is a scaled integer in yen/MWh, and the venue
 * serialises it either bare or wrapped in its newtype.
 */
export type WireVirtualTrade = VirtualTradeFields & {
  price: number | { price: number | null } | null;
};

export type VirtualTrade = VirtualTradeFields & {
  price: Price | null;
};

/** The scaled integer out of either price shape, or null when there is none. */
export const rawTradePrice = (trade: WireVirtualTrade): number | null => {
  const { price } = trade;
  if (price === null || price === undefined) {
    return null;
  }
  // The wrapped shape can itself hold a null.
  return typeof price === "number" ? price : (price.price ?? null);
};

/**
 * Wire fill to stored fill. `null` stays `null` rather than becoming a price of
 * zero, which is what dividing it would have produced.
 */
export const parseVirtualTrade = (wire: WireVirtualTrade): VirtualTrade => {
  const delimiter = wire.price_delimiter ?? undefined;
  recordPriceDelimiter("VIRTUAL_TRADES", delimiter);

  const raw = rawTradePrice(wire);
  return {
    ...wire,
    price_delimiter: delimiter,
    price: raw === null ? null : Price.fromScaledMwh(raw, delimiter),
  };
};

/**
 * Identity of a fill, and the key the store dedups on. `trade_id` alone is not
 * enough: the two legs of a self-trade are two algos publishing the venue's one
 * `trade_id` under their own subjects, and neither subject nor id carries the
 * side.
 */
export const tradeKey = (
  trade: Pick<VirtualTradeFields, "direction" | "trade_id">
): string => `${trade.direction}|${trade.trade_id}`;
